import { Link, useParams } from "react-router-dom";
import { ArrowRight, CheckCircle, Phone } from "@phosphor-icons/react";
import { Reveal } from "../components/Reveal";
import PageHero from "../components/PageHero";
import Seo from "../components/Seo";
import { SERVICES, CONTACT, GALLERY } from "../data/content";

const STEPS = [
  {
    title: "A first chat — no obligation",
    text: "Tell us your plans in plain English. We'll tell you honestly what's possible — no jargon, no pressure.",
  },
  {
    title: "Research & recommendation",
    text: "We search 90+ UK lenders, agree our fee in writing up front, and present the options that genuinely fit.",
  },
  {
    title: "Application to keys",
    text: "We handle the paperwork, chase the lender, and keep you updated by phone, email or WhatsApp — your choice.",
  },
];

const ServicePage = () => {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);

  if (!service) {
    return (
      <main className="bg-brand-bone pt-[76px]">
        <div className="mx-auto max-w-3xl px-5 py-32 text-left sm:px-8">
          <h1 className="editorial-h text-4xl text-brand-ink">Page not found</h1>
          <p className="mt-4 text-neutral-600">That page doesn't exist — but we'd still love to help.</p>
          <Link to="/" data-testid="not-found-home-link" className="mt-8 inline-flex items-center gap-2 text-[0.72rem] font-semibold uppercase tracking-[0.22em] text-brand">
            Back to home <ArrowRight size={14} weight="bold" />
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main data-testid={`service-page-${slug}`} className="bg-brand-bone">
      <Seo
        title={`${service.title} Mortgages`}
        description={service.tagline}
        path={`/mortgages/${slug}`}
        image={service.image}
      />
      <PageHero
        testId={`service-hero-${slug}`}
        eyebrow={`Mortgages · ${service.title}`}
        title={service.title}
        italic={service.tagline.split(".")[0] + "."}
        image={service.image}
      >
        <div className="flex flex-wrap gap-4">
          <Link
            to="/contact"
            data-testid="service-book-call-btn"
            className="group inline-flex items-center gap-2 bg-brand px-8 py-4 text-[0.78rem] font-semibold uppercase tracking-[0.22em] text-white shadow-[0_14px_40px_rgba(1,86,207,0.45)] transition-[background-color,transform] duration-200 hover:bg-brand-dark hover:-translate-y-0.5"
          >
            Book a call
            <ArrowRight size={14} weight="bold" />
          </Link>
          <a
            href={CONTACT.phoneHref}
            data-testid="service-phone-link"
            className="inline-flex items-center gap-2 border border-white/40 px-8 py-4 text-[0.78rem] font-semibold uppercase tracking-[0.22em] text-white transition-[background-color,border-color] duration-200 hover:border-white hover:bg-white/5"
          >
            <Phone size={14} weight="duotone" />
            {CONTACT.phone}
          </a>
        </div>
      </PageHero>

      <section className="bg-white py-24 sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="grid gap-16 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <Reveal>
                <p className="eyebrow text-brand"><span className="rule-line" />How we help</p>
                <h2 className="editorial-h mt-6 text-3xl text-brand-ink sm:text-5xl">
                  The {service.title.toLowerCase()}{" "}
                  <span className="italic text-brand">story.</span>
                </h2>
                <p className="mt-8 text-base leading-relaxed text-neutral-600 sm:text-lg">
                  {service.description}
                </p>
              </Reveal>
              <ul className="mt-12 space-y-4">
                {service.points.map((p, i) => (
                  <Reveal key={p} delay={i * 0.06}>
                    <li className="flex items-start gap-4 border-l-2 border-brand/30 bg-brand-bone p-6 transition-[border-color] duration-200 hover:border-brand">
                      <CheckCircle size={22} weight="duotone" className="mt-0.5 shrink-0 text-brand" />
                      <span className="text-sm leading-relaxed text-neutral-700 sm:text-base">{p}</span>
                    </li>
                  </Reveal>
                ))}
              </ul>

              {service.checklist && (
                <div data-testid="service-checklist" className="mt-16 border border-brand-gold/40 bg-white p-10 sm:p-12">
                  <Reveal>
                    <p className="eyebrow text-brand-gold"><span className="rule-line" />For whom</p>
                    <h3 className="editorial-h mt-6 text-2xl text-brand-ink sm:text-3xl">
                      {service.checklist.heading}
                    </h3>
                  </Reveal>
                  <ol className="mt-10 space-y-6">
                    {service.checklist.items.map((item, i) => (
                      <Reveal key={i} delay={i * 0.08}>
                        <li className="flex items-start gap-5">
                          <span aria-hidden className="editorial-h shrink-0 text-3xl text-brand-gold">
                            0{i + 1}
                          </span>
                          <p className="text-sm leading-relaxed text-neutral-700 sm:text-base">{item}</p>
                        </li>
                      </Reveal>
                    ))}
                  </ol>
                </div>
              )}

              {service.disclaimer && (
                <Reveal>
                  <aside
                    data-testid="service-disclaimer"
                    className="mt-12 border-l-2 border-neutral-300 bg-brand-bone/70 p-6 text-[12px] leading-relaxed text-neutral-600"
                  >
                    <p className="mb-2 text-[0.65rem] font-semibold uppercase tracking-[0.3em] text-neutral-500">
                      Important
                    </p>
                    {service.disclaimer}
                  </aside>
                </Reveal>
              )}
            </div>
            <div className="lg:col-span-5">
              <div className="lg:sticky lg:top-28 space-y-4">
                <Reveal>
                  <p className="eyebrow text-brand"><span className="rule-line" />First call to keys</p>
                  <h3 className="editorial-h mt-6 text-2xl text-brand-ink">Three steps.</h3>
                </Reveal>
                {STEPS.map((s, i) => (
                  <Reveal key={s.title} delay={i * 0.08}>
                    <div className="border border-black/10 bg-brand-bone p-7">
                      <span aria-hidden className="editorial-h text-4xl text-brand/30">
                        0{i + 1}
                      </span>
                      <h3 className="editorial-h mt-3 text-lg text-brand-ink">{s.title}</h3>
                      <p className="mt-3 text-sm leading-relaxed text-neutral-600">{s.text}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative bg-brand-ink py-24 overflow-hidden">
        <img aria-hidden src={GALLERY[(SERVICES.findIndex((s) => s.slug === slug) + 2) % GALLERY.length]} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-ink via-brand-ink/90 to-brand-ink/60" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <p className="eyebrow text-brand-gold"><span className="rule-line" />Talk it through</p>
            <h2 className="editorial-h mt-6 max-w-2xl text-3xl text-white sm:text-5xl">
              Talk it through <span className="italic text-brand-gold">with a human.</span>
            </h2>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-white/70">
              A no-pressure chat about your {service.title.toLowerCase()} plans. One honest fee,
              agreed up front.
            </p>
            <Link
              to="/contact"
              data-testid="service-cta-btn"
              className="group mt-10 inline-flex items-center gap-2 bg-brand px-8 py-4 text-[0.78rem] font-semibold uppercase tracking-[0.22em] text-white transition-[background-color,transform] duration-200 hover:bg-brand-dark hover:-translate-y-0.5"
            >
              Get in touch
              <ArrowRight size={14} weight="bold" className="transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default ServicePage;
